import { useStore, List, useListContext } from 'react-admin';
import { ReactNode, useEffect, useRef } from 'react';

/**
 * URLと同期せずにlocalStorageのみで状態を管理するカスタムListコンポーネント
 *
 * <List>コンポーネントをラップして、storeKeyを使ってlocalStorageに状態を保存し、
 * disableSyncWithLocationでURL同期を無効化する際の状態消失問題を解決します。
 */
interface PersistentListProps {
  resource: string;
  storeKey: string;
  children: ReactNode;
  filter?: any;
  sort?: { field: string; order: string };
  perPage?: number;
  sx?: any;
  [key: string]: any;
}

// localStorageから状態を復元し、変更を保存するコンポーネント
const PersistentListContent = ({
  children,
  storeKey,
  defaultSort
}: {
  children: ReactNode;
  storeKey: string;
  defaultSort: { field: string; order: string };
}) => {
  const listContext = useListContext();
  const [storedSort, setStoredSort] = useStore(`${storeKey}.sort`, defaultSort);
  const [storedPage, setStoredPage] = useStore(`${storeKey}.page`, 1);
  const [storedPerPage, setStoredPerPage] = useStore(`${storeKey}.perPage`, 10);
  const isInitialized = useRef(false);

  // 初回マウント時のみlocalStorageから復元
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      if (storedSort && JSON.stringify(storedSort) !== JSON.stringify(listContext.sort)) {
        listContext.setSort(storedSort);
      }
      if (storedPage !== listContext.page) {
        listContext.setPage(storedPage);
      }
      if (storedPerPage !== listContext.perPage) {
        listContext.setPerPage(storedPerPage);
      }
    }
  }, []);

  // 状態変更を監視してlocalStorageに保存
  useEffect(() => {
    if (isInitialized.current && listContext.sort) {
      setStoredSort(listContext.sort);
    }
  }, [listContext.sort, setStoredSort]);

  useEffect(() => {
    if (isInitialized.current) {
      setStoredPage(listContext.page);
    }
  }, [listContext.page, setStoredPage]);

  useEffect(() => {
    if (isInitialized.current) {
      setStoredPerPage(listContext.perPage);
    }
  }, [listContext.perPage, setStoredPerPage]);

  return <>{children}</>;
};

export const PersistentList = ({
  resource,
  storeKey,
  children,
  filter = {},
  sort: defaultSort = { field: 'id', order: 'ASC' },
  perPage: defaultPerPage = 10,
  sx,
  ...props
}: PersistentListProps) => {
  return (
    <List
      resource={resource}
      filter={filter}
      sort={defaultSort}
      perPage={defaultPerPage}
      disableSyncWithLocation
      sx={sx}
      {...props}
    >
      <PersistentListContent storeKey={storeKey} defaultSort={defaultSort}>
        {children}
      </PersistentListContent>
    </List>
  );
};
